import { RestClient, RestError } from "./restClient";

/** Kind of API key. `embed` keys drive the embeddable chat widget; `org` keys
 *  are headless service keys. */
export type ApiKeyKind = "embed" | "org";

/** How an embed key's end-users authenticate.
 *  - "customer_jwt" : the host app passes a per-user signed JWT (verified against
 *    the key's issuer/JWKS/audience).
 *  - "anonymous"    : no JWT; each visitor is an anonymous subject. */
export type ApiKeyAuthMode = "customer_jwt" | "anonymous" | "backend_mint";

/** Capability allowlist — a subset of the company's INSTALLED components. The
 *  server trims anything the company hasn't installed (key.config ⊆ org install). */
export interface ApiKeyComponents {
  skills?: string[];
  scripts?: string[];
  commands?: string[];
  mcp_servers?: string[];
}

export interface CreateApiKeyInput {
  name: string;
  kind?: ApiKeyKind; // defaults to "embed"
  config?: { components?: ApiKeyComponents };
  allowed_origins?: string[];
  auth_mode?: ApiKeyAuthMode;
  jwt_issuer?: string;
  jwt_jwks_url?: string;
  jwt_audience?: string;
  model?: string | null;
  rate_limit_rpm?: number | null;
}

/** Create response — the plaintext `key` is returned ONCE here and never again. */
export interface CreatedApiKey {
  id: string;
  prefix: string;
  key: string; // PLAINTEXT — shown once, never persisted client-side
  name: string;
  kind: ApiKeyKind;
  auth_mode: ApiKeyAuthMode; // drives which embed snippets are safe to show
}

/** A listed key. NEVER carries the hash or plaintext. */
export interface ApiKeyListing {
  id: string;
  name: string;
  key_prefix: string;
  kind: ApiKeyKind;
  config: { components?: ApiKeyComponents } | null;
  allowed_origins: string[];
  auth_mode: ApiKeyAuthMode;
  model: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface PatchApiKeyInput {
  name?: string;
  config?: { components?: ApiKeyComponents };
  allowed_origins?: string[];
  model?: string | null;
}

/** Admin client for company-scoped API/embed keys (apikeys:manage-gated). */
export class ApiKeysApi {
  constructor(private client: RestClient) {}

  /** List the company's keys. 404-tolerant (feature disabled → empty). */
  async list(): Promise<ApiKeyListing[]> {
    try {
      const r = await this.client.get<{ keys: ApiKeyListing[] } | ApiKeyListing[]>(
        "/v1/api-keys",
      );
      return Array.isArray(r) ? r : (r.keys ?? []);
    } catch (e) {
      if (e instanceof RestError && e.status === 404) return [];
      throw e;
    }
  }

  /** The plugin/component collection a key can be scoped to (org install
   *  ceiling), keyed by kind. Feeds the create-key picker. */
  async grantablePlugins(): Promise<Record<string, string[]>> {
    try {
      const r = await this.client.get<{ components: Record<string, string[]> }>(
        "/v1/api-keys/grantable-plugins",
      );
      return r.components ?? {};
    } catch (e) {
      if (e instanceof RestError && e.status === 404) return {};
      throw e;
    }
  }

  /** Create a key. The returned `key` is plaintext, shown ONCE. */
  async create(input: CreateApiKeyInput): Promise<CreatedApiKey> {
    return this.client.post<CreatedApiKey>("/v1/api-keys", {
      kind: "embed",
      ...input,
    });
  }

  async patch(id: string, input: PatchApiKeyInput): Promise<void> {
    await this.client.patch<{ ok: boolean }>(`/v1/api-keys/${id}`, input);
  }

  /** Revoke (soft-delete) a key. */
  async revoke(id: string): Promise<void> {
    await this.client.delete<{ ok: boolean }>(`/v1/api-keys/${id}`);
  }

  /** Rotate a key's secret in place. Same id, new prefix + plaintext. The
   *  plaintext is returned ONCE — same reveal-once contract as create. */
  async rotate(id: string): Promise<{ id: string; prefix: string; key: string }> {
    return this.client.post<{ id: string; prefix: string; key: string }>(
      `/v1/api-keys/${id}/rotate`,
      {},
    );
  }
}
